"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { createClient } from "@/lib/supabase";
import type { Profile, Experience, Education, Skill, Certificate } from "@/types/database";
import type { CVData, CVTemplateId } from "@/components/cv/types";
import { CV_TEMPLATES } from "@/components/cv/types";
import { CVTemplateRenderer } from "@/components/cv/templates";

const TEMPLATE_PREVIEW_STYLES: Record<CVTemplateId, React.CSSProperties> = {
  modern: {
    background: "linear-gradient(135deg, #0f3460 0%, #16213e 100%)",
  },
  minimal: {
    background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    border: "1px solid #cbd5e1",
  },
  classic: {
    background: "linear-gradient(135deg, #fafaf9 0%, #e7e5e4 100%)",
  },
  tech: {
    background: "linear-gradient(135deg, #1e293b 0%, #3B82F6 50%)",
  },
};

export default function CVPage() {
  const [cvData, setCvData] = useState<CVData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplateId | null>(null);

  useEffect(() => {
    const supabase = createClient();
    Promise.all([
      supabase.from("profile").select("*").limit(1).single(),
      supabase.from("experiences").select("*").order("sort_order"),
      supabase.from("education").select("*").order("sort_order"),
      supabase.from("skills").select("*").order("sort_order"),
      supabase.from("certificates").select("*").order("sort_order"),
    ]).then(([p, exp, edu, sk, cert]) => {
      const profile = p.data as Profile | null;
      if (!profile) {
        setLoading(false);
        return;
      }
      setCvData({
        profile,
        experiences: (exp.data as Experience[]) ?? [],
        education: (edu.data as Education[]) ?? [],
        skills: (sk.data as Skill[]) ?? [],
        certificates: (cert.data as Certificate[]) ?? [],
      });
      setLoading(false);
    });
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-[var(--text-muted)]">Đang tải dữ liệu CV...</div>
      </div>
    );
  }

  if (!cvData) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-red-500">Không tìm thấy thông tin profile.</div>
      </div>
    );
  }

  // Đã chọn template → hiển thị CV preview + nút xuất
  if (selectedTemplate) {
    return (
      <>
        <div
          className="fixed top-4 right-4 z-50 flex gap-2 print:hidden"
          data-print-hide
        >
          <button
            onClick={handlePrint}
            className="rounded-lg bg-[var(--dev-blue)] px-6 py-2.5 font-medium text-white shadow-lg transition-colors hover:bg-[var(--dev-blue-light)]"
          >
            🖨️ In / Lưu PDF
          </button>
          <button
            onClick={() => setSelectedTemplate(null)}
            className="rounded-lg border border-[var(--text-muted)] bg-transparent px-4 py-2.5 text-[var(--text-secondary)] transition-colors hover:bg-white/5"
          >
            ← Đổi mẫu
          </button>
          <Link
            href="/"
            className="rounded-lg border border-[var(--text-muted)] bg-transparent px-4 py-2.5 text-[var(--text-secondary)] transition-colors hover:bg-white/5"
          >
            Về trang chủ
          </Link>
        </div>

        <div className="mx-auto max-w-4xl px-4 py-8 print:py-0">
          <div className="overflow-hidden rounded-xl bg-white shadow-xl print:rounded-none print:shadow-none">
            <CVTemplateRenderer templateId={selectedTemplate} data={cvData} />
          </div>
        </div>
      </>
    );
  }

  // Chưa chọn template → hiển thị grid chọn mẫu
  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">
          Xuất CV
        </h1>
        <p className="mt-2 text-[var(--text-muted)]">
          Chọn mẫu template phù hợp để xuất CV của bạn
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {CV_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            onClick={() => setSelectedTemplate(tpl.id)}
            className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left transition-all hover:border-[var(--dev-blue)]/50 hover:bg-white/10 hover:shadow-lg hover:shadow-[var(--dev-blue)]/10"
          >
            <div
              className="flex h-32 items-center justify-center p-4 transition-transform group-hover:scale-[1.02]"
              style={TEMPLATE_PREVIEW_STYLES[tpl.id]}
            >
              <span
                className={`text-lg font-semibold ${
                  tpl.id === "minimal" || tpl.id === "classic"
                    ? "text-gray-800"
                    : "text-white"
                }`}
              >
                {tpl.name}
              </span>
            </div>
            <div className="flex flex-1 flex-col p-4">
              <h3 className="font-semibold text-[var(--text-primary)]">
                {tpl.name}
              </h3>
              <p className="mt-1 flex-1 text-sm text-[var(--text-muted)]">
                {tpl.description}
              </p>
              <span className="mt-3 inline-flex items-center text-sm text-[var(--dev-blue)]">
                Xem trước & xuất →
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Link
          href="/"
          className="text-sm text-[var(--text-muted)] underline transition-colors hover:text-[var(--text-secondary)]"
        >
          ← Về trang chủ
        </Link>
      </div>
    </div>
  );
}
